import { getActiveSource } from "./resumeLinks";
import whenIdle from "./whenIdle";

let initialized = false;
let initPromise = null;

function isAnalyticsEnabled() {
    return Boolean(process.env.REACT_APP_POSTHOG_KEY);
}

function getPostHogHost() {
    return process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";
}

function loadPostHog() {
    if (window.posthog?.__loaded) {
        return Promise.resolve(window.posthog);
    }

    if (initPromise) {
        return initPromise;
    }

    // Load after the intro loader (~2.1s) has finished: evaluating the PostHog script mid-animation causes a visible hitch.
    initPromise = whenIdle()
        .then(() => new Promise((wait) => setTimeout(wait, 3000)))
        .then(() => new Promise((resolve, reject) => {
        if (!window.posthog) {
            /* eslint-disable */
            !(function (document, posthog) {
                if (posthog.__SV) {
                    return;
                }

                window.posthog = posthog;
                posthog._i = [];
                posthog.init = function (token, config, name) {
                    function createMethod(object, methodName) {
                        const parts = methodName.split(".");
                        if (parts.length === 2) {
                            object = object[parts[0]];
                            methodName = parts[1];
                        }

                        object[methodName] = function () {
                            object.push([methodName].concat(Array.prototype.slice.call(arguments, 0)));
                        };
                    }

                    const script = document.createElement("script");
                    script.type = "text/javascript";
                    script.async = true;
                    script.src =
                        config.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") +
                        "/static/array.js";

                    const firstScript = document.getElementsByTagName("script")[0];
                    firstScript.parentNode.insertBefore(script, firstScript);

                    let instance = posthog;
                    if (name !== undefined) {
                        instance = posthog[name] = [];
                    } else {
                        name = "posthog";
                    }

                    instance.people = instance.people || [];
                    instance.toString = function (includeStub) {
                        let label = "posthog";
                        if (name !== "posthog") {
                            label += "." + name;
                        }
                        if (!includeStub) {
                            label += " (stub)";
                        }
                        return label;
                    };
                    instance.people.toString = function () {
                        return instance.toString(1) + ".people (stub)";
                    };

                    const methods =
                        "init capture register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(
                            " "
                        );

                    for (let index = 0; index < methods.length; index += 1) {
                        createMethod(instance, methods[index]);
                    }

                    posthog._i.push([token, config, name]);
                };
                posthog.__SV = 1;
            })(document, window.posthog || []);
            /* eslint-enable */
        }

        window.posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
            api_host: getPostHogHost(),
            capture_pageview: false,
            autocapture: false,
            disable_session_recording: true,
            persistence: "localStorage+cookie",
            loaded: (client) => {
                client.__loaded = true;
                resolve(client);
            },
        });

        window.setTimeout(() => {
            if (!window.posthog?.__loaded) {
                reject(new Error("PostHog failed to load"));
            }
        }, 10000);
    }));

    return initPromise;
}

function registerSource(client) {
    const source = getActiveSource();
    if (source !== "direct") {
        client.register({ resume_source: source });
    }
    return source;
}

export function initAnalytics() {
    if (initialized || !isAnalyticsEnabled()) {
        return;
    }

    loadPostHog()
        .then((client) => {
            registerSource(client);
            initialized = true;
        })
        .catch(() => {
            initPromise = null;
        });
}

export function trackPageView(pathname = window.location.pathname) {
    if (!isAnalyticsEnabled()) {
        return;
    }

    loadPostHog()
        .then((client) => {
            const source = registerSource(client);

            client.capture("$pageview", {
                source,
                path: pathname,
                page: pathname,
                $current_url: window.location.href,
                $pathname: window.location.pathname,
                $host: window.location.host,
            });

            initialized = true;
        })
        .catch(() => {
            initPromise = null;
        });
}

export function trackResumeEvent(eventName, properties = {}) {
    if (!isAnalyticsEnabled()) {
        return;
    }

    loadPostHog()
        .then((client) => {
            client.capture(eventName, {
                source: getActiveSource(),
                ...properties,
            });

            initialized = true;
        })
        .catch(() => {
            initPromise = null;
        });
}
